import {Component, OnDestroy, OnInit} from '@angular/core';
import {BsToastService} from './Services/bs-toast-service';
import {NavigationEnd, NavigationStart, Router} from '@angular/router';
import {AppConfigService} from './Services/app-config.service';
import {AuthService} from './Services/auth.service';
import {InactivityService} from './Services/inactivity.service';
import {PreferencesService} from './Services/preferences.service';
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';

@Component({
  selector: 'app-root',
  template: `
    <app-bs-toaster></app-bs-toaster>
    <router-outlet></router-outlet>
  `,
  styleUrls: ['./app.component.css'],
  standalone: false
})
export class AppComponent implements OnDestroy, OnInit {
  title = 'app';

  // Routing/cleanup
  routerSubscription: Subscription;

  // Auto‑refresh guard to avoid multiple timers
  private autoRefreshStarted = false;
  private readonly activityEvents = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click', 'wheel'];
  private readonly onUserActivity = () => {
    this.auth.markSessionActivity();
  };

  constructor(
    private toast: BsToastService,
    private router: Router,
    private appConfig: AppConfigService,
    private inactivityService: InactivityService,
    private preferences: PreferencesService,
    public auth: AuthService,
  ) {
    // Subscribe to routing events (keep logic minimal and non‑blocking)
    this.routerSubscription = this.router.events.pipe().subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (event.navigationTrigger === 'popstate') {
          // back button clicked (placeholder for any special handling)
        }
      }
    });

    // Start tracking inactivity in ngOnInit instead of constructor to avoid early injection issues
    // this.inactivityService.startTracking();
  }

  ngOnInit() {
    // Start tracking inactivity
    this.inactivityService.startTracking();
    // Apply global theme defaults
    document.body.setAttribute('data-bs-theme', this.appConfig.getEffectiveTheme());
    const savedLayout = localStorage.getItem('layout') || 'vertical';
    document.documentElement.setAttribute('data-layout', savedLayout);

    // Wake-up handlers to mitigate background tab throttling for token refresh
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('focus', this.handleWindowFocus);
    this.activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, this.onUserActivity, {passive: true});
    });

    // Case 1: Page refresh with an existing user
    if (localStorage.getItem('currentUser')) {
      this.preferences.RestoreFromLocalStorage();
      // console.log('case 1 start token auto refresh timer');
      this.auth.startTokenAutoRefresh(30000);
      this.autoRefreshStarted = true;
      this.auth.markSessionActivity(true);
      // Force an immediate check in case token is near expiry on load
      this.auth.checkAndRefreshNow();
    }

    // Case 2: Fresh login within the SPA – start on accessToken$ emission
    this.auth.accessToken$
      .pipe(filter((token) => !!token && !this.autoRefreshStarted))
      .subscribe(() => {
        // console.log('case 2 starting auto refresh timer');
        this.auth.startTokenAutoRefresh(30000);
        this.autoRefreshStarted = true;
        // Force an immediate check to handle near-expiry tokens
        this.auth.checkAndRefreshNow();
        // Also start idle watcher now that the user is authenticated
        this.inactivityService.startTracking();
      });

    // Case 3: Fallback – on the first navigation after login in the same tab
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((evt: NavigationEnd) => {
        // If navigating to login, ensure idle watcher and UI are stopped/cleared
        if (evt.urlAfterRedirects?.includes('login') || evt.url?.includes('login')) {
          this.inactivityService.stopTracking();
          this.autoRefreshStarted = false; // do not run auto refresh on login page
          this.auth.stopTokenAutoRefresh();
          return;
        }

        if (!this.autoRefreshStarted && localStorage.getItem('currentUser')) {
          // console.log(' case 3 starting auto refresh timer');
          this.auth.startTokenAutoRefresh(30000);
          this.autoRefreshStarted = true;
          // Start idle watcher as well on first navigation after login
          this.inactivityService.startTracking();
        }
      });

    // Cross‑tab start/stop via localStorage events
    window.addEventListener('storage', (e) => {
      if (e.key === this.auth.getCrossTabActivityStorageKey()) {
        if (!this.router.url.includes('login') && this.auth.isAuthenticated()) {
          this.inactivityService.startTracking();
        }
        return;
      }

      if (e.key === 'currentUser') {
        if (e.newValue) {
          // User logged in in another tab – start auto refresh and idle watcher
          if (!this.autoRefreshStarted) {
            this.auth.startTokenAutoRefresh(30000);
            this.autoRefreshStarted = true;
            this.auth.checkAndRefreshNow();
          }
          this.inactivityService.startTracking();
        } else {
          // User logged out in another tab – stop idle watcher and hide modal
          this.inactivityService.stopTracking();
          this.autoRefreshStarted = false;
          this.auth.stopTokenAutoRefresh();
        }
      }
    });

    // Microtask/short-delay fallback to cover init races (deep links)
    setTimeout(() => {
      if (!this.autoRefreshStarted && localStorage.getItem('currentUser') && this.auth.isAuthenticated()) {
        this.auth.startTokenAutoRefresh(30000);
        this.autoRefreshStarted = true;
        this.auth.checkAndRefreshNow();
      }
    }, 0);
  }

  // Logout from idle modal or user action
  async logOut() {
    // Stop idle watcher immediately
    this.inactivityService.stopTracking();

    // Stop interval and reset guard so a later login can start it again
    this.auth.stopTokenAutoRefresh();
    this.autoRefreshStarted = false;

    await this.auth.logout(true);
  }

  // Handler: invoked when tab visibility changes back to visible
  private handleVisibilityChange = () => {
    if (document.visibilityState !== 'visible') {
      return;
    }
    // Do nothing on login page
    if (this.router.url.includes('login')) {
      return;
    }
    if (this.auth.isAuthenticated()) {
      this.auth.markSessionActivity();
      if (!this.autoRefreshStarted) {
        this.auth.startTokenAutoRefresh(30000);
        this.autoRefreshStarted = true;
      }
      // Force an immediate check/refresh in case the background interval was throttled
      this.auth.checkAndRefreshNow();
    }
  }

  // Handler: invoked when window regains focus
  private handleWindowFocus = () => {
    // Do nothing on login page
    if (this.router.url.includes('login')) {
      return;
    }
    if (this.auth.isAuthenticated()) {
      this.auth.markSessionActivity();
      if (!this.autoRefreshStarted) {
        this.auth.startTokenAutoRefresh(30000);
        this.autoRefreshStarted = true;
      }
      this.auth.checkAndRefreshNow();
    }
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    // Remove wake-up listeners
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('focus', this.handleWindowFocus);
    this.activityEvents.forEach((eventName) => {
      window.removeEventListener(eventName, this.onUserActivity);
    });
  }
}
